<script lang="ts">
    import { portal } from "svelte-portal"
    import {
        globalSelectHandler,
        SelectionType,
        type GlobalSelection,
        Grabber,
        TransferHighlightMode,
    } from "@utils/highlights/directive-logic/global-select-handler"
    import { onDestroy, onMount } from "svelte"
    import PopupDialogue from "@components/popupSelection/PopupDialogue.svelte"
    import { findHighlight } from "@store/highlights"
    import Handle from "./Handle.svelte"
    import NoteEditor from "./NoteEditor.svelte"
    import { calculateOffset } from "@utils/helpers/offset-top-recursive"
    import { ContextMenuViewportSelection } from "@utils/highlights/directive-logic/highlight-listener-permissions"
    import { AnimationFramePender } from "@utils/helpers/penders"
    import { writable } from "svelte/store"
    import { tooltip } from "@utils/tooltip"
    import { IMAGE_QUOTE_ALLOWED, isQuoteAllowed } from "@utils/image-editor/image-quote"

    const viewportStyleFramePender = new AnimationFramePender()

    let selection: GlobalSelection | null = null
    let contextElement: HTMLElement | null = null
    let attachElement: HTMLElement | null = null
    let offsetTop: number | null = null
    let offsetLeft: number | null = null
    const viewportOffset = writable({ x: 0, y: 0 })
    const setSelect = ({ detail }: { detail: { selection: GlobalSelection } }) => {
        viewportStyleFramePender.cancel()
        selection = detail.selection
        if (contextElement !== selection.contextElement) contextElement = selection.contextElement

        offsetTop = selection
            ? calculateOffset(selection.parentElement, selection.contextElement, "offsetTop")
            : null
        offsetLeft = selection
            ? calculateOffset(selection.parentElement, selection.contextElement, "offsetLeft")
            : null
        // See what kind of viewport style we're using
        if (
            selection.permissions.viewportSelection ===
            ContextMenuViewportSelection.SCROLL_CONTAINER
        ) {
            $viewportOffset = { x: 0, y: 0 }
            attachElement = contextElement
        } else {
            attachElement = document.body
            // Viewport style updating
            viewportStyleFramePender.overwrite(refreshViewportStyle)
        }
    }

    /**
     * This is only used when we're in viewport style mode (see {@link ContextMenuViewportSelection})
     */
    function refreshViewportStyle() {
        if (selection === null) {
            // throw new Error("Selection was null in animation frame, did you cancel?")
            console.warn("Selection was null in animation frame, did you cancel?")
            return
        }
        const { x, y } = selection.parentElement.getBoundingClientRect()
        $viewportOffset = { x, y }

        // Set the loop
        viewportStyleFramePender.overwrite(refreshViewportStyle)
    }

    const deselect = () => {
        selection = null
    }

    onMount(() => {
        globalSelectHandler.addEventListener("select", setSelect)
        globalSelectHandler.addEventListener("deselect", deselect)
    })

    onDestroy(() => {
        globalSelectHandler.removeEventListener("select", setSelect)
        globalSelectHandler.removeEventListener("deselect", deselect)
    })

    //
    // Manage Highlights and interactions
    //

    function highlight() {
        if (selection === null) throw new Error("Attempting to highlight a null selection")
        if (selection.type === SelectionType.HIGHLIGHT) {
            findHighlight(selection.highlightID)?.changeHighlighter()
        } else {
            globalSelectHandler.createHighlight()
        }
    }

    function deletehighlight() {
        globalSelectHandler.deleteHighlight()
    }

    // Pointer / grabber actions
    const SCROLL_STOPPER_SYMBOL = Symbol()
    function start(grabber: Grabber) {
        globalSelectHandler.startMove(grabber)
        if (selection === null) throw new Error("Started dragging but selection was not set")
        selection.scrollLock.lock(SCROLL_STOPPER_SYMBOL)
    }

    function doneMove() {
        globalSelectHandler.doneMove()
        if (selection === null) throw new Error("Started dragging but selection was not set")
        selection.scrollLock.unlock(SCROLL_STOPPER_SYMBOL)
    }

    function move(clientX: number, clientY: number) {
        // Get offset to `parentElement`, which is what the
        // HighlightListener's x,y co-ordinates are based off

        if (globalSelectHandler.selection === null)
            throw new Error("Grabber movement detected but global selection not set")

        const { x: parentX, y: parentY } =
            globalSelectHandler.selection.parentElement.getBoundingClientRect()

        globalSelectHandler.triggerMove(clientX - parentX, clientY - parentY)
    }

    let noteOpen = false
    function note() {
        if (selection === null)
            throw new Error("Note was pressed but there was no selection was set")
        if (selection.type === SelectionType.TEXT) {
            globalSelectHandler.createHighlight(TransferHighlightMode.KEEP_SELECTION)
        }
        noteOpen = true
    }

    //
    // CONTEXT MENU ACTIONS
    //

    /**
     * TODO : Implement the drag to scroll
     */
    // // Logic for scrolling container (whe)
    // const VerticalPusher = new AnimationRunner<number>((num: number) => {
    //     scrollContainer.scrollBy(0, num)
    // }, 0)

    // function verticalPush(e: CustomEvent<{ amplitude: number }>) {
    //     VerticalPusher.start(e.detail.amplitude)
    // }

    // // Logic for scrolling container
    // function verticalPushStop(e: CustomEvent<null>) {
    //     VerticalPusher.stop()
    // }

    onDestroy(() => {
        viewportStyleFramePender.cancel()
    })

    // Make sure you can't do image quotes in supplementary material
    $: canQuoteImage = selection && selection.start && isQuoteAllowed(selection.start.path)
</script>

{#if selection !== null && attachElement && offsetLeft !== null && offsetTop !== null}
    <div
        class="{attachElement === document.body
            ? 'z-10'
            : 'z-2'} inset-0 absolute pointer-events-none"
        use:portal={attachElement}
    >
        <PopupDialogue
            {canQuoteImage}
            grabberState={selection.grabberState}
            highlightID={selection.type === SelectionType.HIGHLIGHT ? selection.highlightID : null}
            on:highlight={() => {
                highlight()
                globalSelectHandler.cancelAll()
            }}
            on:share={() => globalSelectHandler.share()}
            on:copy={() => {
                tooltip("Copied!")
                globalSelectHandler.copy()
            }}
            on:copyalt={() => {
                tooltip("Copied alternative!")
                globalSelectHandler.copy(true)
            }}
            on:image={() => {
                if (IMAGE_QUOTE_ALLOWED) {
                    if (canQuoteImage) {
                        globalSelectHandler.image()
                    } else {
                        tooltip("Can't create images in supplementary material")
                    }
                } else {
                    tooltip("Your device does not support image creation")
                }
            }}
            on:note={note}
            on:deletehighlight={deletehighlight}
            permissions={selection.permissions}
            pusherHeight={selection.top + (offsetTop ?? 0) + $viewportOffset.y}
        />
        <!-- Handlers -->
        <Handle
            grabberState={selection.grabberState}
            coordinates={selection.bars.left}
            offsetLeft={offsetLeft + $viewportOffset.x}
            offsetTop={offsetTop + $viewportOffset.y}
            grabber={Grabber.LEFT}
            on:start={() => start(Grabber.LEFT)}
            on:move={({ detail }) => move(detail.clientX, detail.clientY)}
            on:done={doneMove}
        />
        <Handle
            grabberState={selection.grabberState}
            coordinates={selection.bars.right}
            offsetLeft={offsetLeft + $viewportOffset.x}
            offsetTop={offsetTop + $viewportOffset.y}
            grabber={Grabber.RIGHT}
            on:start={() => start(Grabber.RIGHT)}
            on:move={({ detail }) => move(detail.clientX, detail.clientY)}
            on:done={doneMove}
        />
    </div>
{/if}
{#if noteOpen && selection !== null && selection.type === SelectionType.HIGHLIGHT}
    <NoteEditor on:exit={() => (noteOpen = false)} highlightID={selection.highlightID} />
{/if}
